// ContactPage.jsx – Polished Firestore Integrated Version
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Clock, Globe, Send } from "lucide-react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase1";

// ---------------- Animations ----------------
const headerVariants = {
  hidden: { opacity: 0, y: -40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const formVariants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { delay: 0.4, duration: 0.6 },
  },
};

const ContactPage = () => {
  // ---------------- State ----------------
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // ---------------- Handlers ----------------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);

    try {
      await addDoc(collection(db, "contact_messages"), {
        ...formData,
        createdAt: serverTimestamp(),
        source: "contact-page",
      });

      setFormData({ name: "", email: "", subject: "", message: "" });
      setSuccess(true);
    } catch (err) {
      console.error("Contact form error:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Contact Cards ----------------
  const contactMethods = [
    {
      icon: Mail,
      title: "Email Us",
      value: "talkytoonofficial@gmail.com",
      detail: "Questions, feedback, partnerships or support",
      bg: "bg-purple-100",
      color: "text-purple-600",
      link: "mailto:talkytoonofficial@gmail.com",
    },
    {
      icon: Clock,
      title: "Response Time",
      value: "24–48 Hours",
      detail: "We usually reply on weekdays",
      bg: "bg-teal-100",
      color: "text-teal-600",
    },
  ];

  // ---------------- UI ----------------
  return (
    <motion.div
      className="min-h-screen w-full bg-gradient-to-br from-indigo-50 via-sky-50 to-white"
      initial="hidden"
      animate="visible"
    >
      <main className="pt-14 pb-24 px-4 sm:px-6 lg:px-8">
        {/* ---------------- Hero ---------------- */}
        <header className="text-center mb-20">
          <motion.div variants={headerVariants} className="max-w-4xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 to-sky-600">
              Get in Touch
            </h1>
            <p className="mt-6 text-xl md:text-2xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
              Have a question or need help? We’re always happy to support parents
              and educators.
            </p>
          </motion.div>
        </header>

        {/* ---------------- Content ---------------- */}
        <section className="max-w-7xl mx-auto bg-white rounded-3xl shadow-2xl p-6 md:p-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* -------- Left: Info -------- */}
          <div className="space-y-8">
            <h3 className="text-3xl font-extrabold text-gray-900">
              Quick Contacts
            </h3>

            {contactMethods.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 + i * 0.15 }}
                  className="flex gap-4 p-4 rounded-xl hover:shadow-md transition"
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${item.bg}`}
                  >
                    <Icon className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{item.title}</p>
                    {item.link ? (
                      <a
                        href={item.link}
                        className="text-indigo-600 font-medium hover:underline"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-indigo-600 font-medium">
                        {item.value}
                      </p>
                    )}
                    <p className="text-sm text-gray-500">{item.detail}</p>
                  </div>
                </motion.div>
              );
            })}

            <div className="pt-6 border-t">
              <p className="font-bold flex items-center gap-2 text-gray-900">
                <Globe className="w-5 h-5 text-indigo-500" />
                Headquarters
              </p>
              <p className="text-gray-600 mt-1 leading-relaxed">
                TalkyToon is an online learning platform. Please contact us via
                email for any inquiries.
              </p>
            </div>
          </div>

          {/* -------- Right: Form -------- */}
          <motion.div
            variants={formVariants}
            className="lg:col-span-2 bg-gray-50 rounded-2xl p-6 md:p-10 border"
          >
            <h3 className="text-3xl font-extrabold text-gray-900 mb-6">
              Send Us a Message
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5">
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                required
                className="w-full p-4 rounded-xl border focus:ring-2 focus:ring-indigo-400 text-lg"
              />

              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email Address"
                required
                className="w-full p-4 rounded-xl border focus:ring-2 focus:ring-indigo-400 text-lg"
              />

              <input
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Subject"
                required
                className="w-full p-4 rounded-xl border focus:ring-2 focus:ring-indigo-400 text-lg"
              />

              <textarea
                name="message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                placeholder="Write your message here…"
                required
                className="w-full p-4 rounded-xl border focus:ring-2 focus:ring-indigo-400 text-lg"
              />

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xl rounded-xl shadow-lg disabled:opacity-60"
              >
                <Send className="w-6 h-6" />
                {loading ? "Sending…" : "Submit Message"}
              </button>

              {success && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-green-600 font-semibold text-center mt-4"
                >
                  ✅ Thank you! Your message has been sent successfully.
                </motion.p>
              )}
            </form>
          </motion.div>
        </section>
      </main>
    </motion.div>
  );
};

export default ContactPage;
